// Shared design tokens for consistent theming across Tailwind config and tailwind-merge
// This file is the single source of truth for color palette and font sizes

export const colorPalette = {
  "north-sea": {
    50: "#E8F2FD",
    100: "#D0E4FB",
    200: "#97C4F6",
    300: "#5AA1F2",
    400: "#3A8EEE",
    500: "#126ED8",
    600: "#0F59AD",
    700: "#0B4383",
    800: "#072F5F",
    900: "#04182F",
    DEFAULT: "#0B4383",
  },
  loch: {
    50: "#E6F5FE",
    100: "#C4E8FD",
    200: "#9CD8FC",
    300: "#6BC5FA",
    400: "#2FAEF8",
    500: "#0798EE",
    600: "#0677B9",
    700: "#05598B",
    800: "#034268",
    900: "#03263B",
    DEFAULT: "#0677B9",
  },
  pond: {
    50: "#e5fbff",
    100: "#c7f7ff",
    200: "#94efff",
    300: "#6be9ff",
    400: "#33e2ff",
    500: "#00c9eb",
    600: "#00a2bd",
    700: "#00768a",
    800: "#004b57",
    900: "#003138",
    DEFAULT: "#00768a",
  },
  coal: "#14171B",
  glen: {
    50: "#e8fcf7",
    100: "#d2f9ef",
    200: "#a4f4df",
    300: "#6eedcc",
    400: "#49e9be",
    500: "#1bdaa7",
    600: "#15ac84",
    700: "#108465",
    800: "#0b5843",
    900: "#052c22",
    DEFAULT: "#108465",
  },
  autumn: {
    50: "#fff1e5",
    100: "#ffe3cc",
    200: "#ffc799",
    300: "#ffa75c",
    400: "#ff8b29",
    500: "#f26f00",
    600: "#c25901",
    700: "#994600",
    800: "#643602",
    900: "#331b00",
    DEFAULT: "#c25901",
  },
  brick: {
    50: "#fce9e9",
    100: "#f9d2d2",
    200: "#f2a6a6",
    300: "#ec7979",
    400: "#e54444",
    500: "#df1f1f",
    600: "#ba1a1a",
    700: "#8c1313",
    800: "#5d0d0d",
    900: "#320606",
    DEFAULT: "#ba1a1a",
  },
  neutral: {
    white: "#FFFFFF",
    50: "#f8f8f9",
    100: "#dce0e5",
    200: "#c7cdd5",
    300: "#a8b3bf",
    400: "#8b96a4",
    500: "#637386",
    600: "#4f5c6b",
    700: "#3f4a56",
    800: "#2f3740",
    900: "#14171b",
    black: "#000000",
    DEFAULT: "#4f5c6b",
  },
  legacy: {
    aqua: "#00AEEF",
    "dark-blue": "#002041",
    primary: "#002E5D",
    navy: "#003362",
    "pastel-blue": "#B3C2D0",
    autumn: "#C15901",
    jungle: "#1B9C85",
    "light-red": "#D76060",
    "dark-red": "#D81703",
    coral: "#FF808B",
    "electric-purple": "#961BFF",
    "teal-blue": "#00859B",
    "light-grey": "#D3D3D3",
  },
  yellow: "#FBBC05",
  slate: "#2f3740",
  "light-slate": "#637385",
  "foggy-firth": "#dce0e5",
  cloud: "#f8f8f9",
  black: "#000000",
} as const;

// Extract color names for tailwind-merge configuration
export const customColorNames = [
  "north-sea",
  "loch",
  "pond",
  "coal",
  "glen",
  "autumn",
  "brick",
  "neutral",
  "yellow",
  "slate",
  "light-slate",
  "foggy-firth",
  "cloud",
  "black",
] as const;

// Extract color variants (e.g., "north-sea-50", "loch-700")
export const customColorVariants = [
  "north-sea-50",
  "north-sea-100",
  "north-sea-200",
  "north-sea-300",
  "north-sea-400",
  "north-sea-500",
  "north-sea-600",
  "north-sea-700",
  "north-sea-800",
  "north-sea-900",
  "loch-50",
  "loch-100",
  "loch-200",
  "loch-300",
  "loch-400",
  "loch-500",
  "loch-600",
  "loch-700",
  "loch-800",
  "loch-900",
  "pond-50",
  "pond-100",
  "pond-200",
  "pond-300",
  "pond-400",
  "pond-500",
  "pond-600",
  "pond-700",
  "pond-800",
  "pond-900",
  "glen-50",
  "glen-100",
  "glen-200",
  "glen-300",
  "glen-400",
  "glen-500",
  "glen-600",
  "glen-700",
  "glen-800",
  "glen-900",
  "autumn-50",
  "autumn-100",
  "autumn-200",
  "autumn-300",
  "autumn-400",
  "autumn-500",
  "autumn-600",
  "autumn-700",
  "autumn-800",
  "autumn-900",
  "brick-50",
  "brick-100",
  "brick-200",
  "brick-300",
  "brick-400",
  "brick-500",
  "brick-600",
  "brick-700",
  "brick-800",
  "brick-900",
  "neutral-white",
  "neutral-50",
  "neutral-100",
  "neutral-200",
  "neutral-300",
  "neutral-400",
  "neutral-500",
  "neutral-600",
  "neutral-700",
  "neutral-800",
  "neutral-900",
  "neutral-black",
] as const;

// Custom font sizes
export const customFontSizes = [
  "display",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "body-xl",
  "body-l",
  "body-md",
  "body-s",
  "body-xs",
  "small-display",
  "small-h1",
  "small-h2",
  "small-h3",
  "small-h4",
  "small-h5",
  "small-h6",
  "small-body-xl",
  "small-body-l",
  "small-body-md",
  "small-body-s",
] as const;
